import React, { useEffect, useMemo, useState } from 'react';
import { Button, Container, Form, Table } from 'react-bootstrap';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  listarFundosPj,
  listarPrestadoresPj,
  obterSaldosFundosBulkPj,
  obterSaldosFundosPj,
} from '~/Services/Service';
import ModalSaque from './components/ModalSaque';
import GridPagination from '../components/GridPagination';
import SortableHeader, {
  alternarOrdenacao,
  ordenarRegistros,
} from '../components/SortableHeader';
import '../styles.css';
import { formatarCompetencia } from '../Prestadores/utils/formatters';

const PAGE_SIZE = 10;

const fundos = [
  { key: '3', label: '13ª NF' },
  { key: '4', label: '14ª NF' },
  { key: 'F', label: 'Fundo Indenizatório' },
];

const tiposMovimento = {
  E: 'Entrada',
  S: 'Saque',
  C: 'Correção',
};

const moeda = valor => Number(valor || 0).toLocaleString('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const colunasOrdenacao = {
  competencia: item => item.competencia || '',
  data: item => item.data_movimento || '',
  tipo: item => tiposMovimento[item.tipo_movimento] || item.tipo_movimento || '',
  ipca: item => Number(item.indice_ipca || 0),
  valor: item => Number(item.valor || 0),
  saldo: item => Number(item.saldo || 0),
  observacao: item => item.observacao || '',
};

const dataBr = value => {
  if (!value) return '-';
  const data = new Date(value);
  if (Number.isNaN(data.getTime())) return '-';
  return data.toLocaleDateString('pt-BR');
};

// Fator IPCA (ex.: 0.003618) -> "0,3618%". Só vem preenchido nas correções ('C').
const formatarIpca = valor => {
  if (valor == null) return '-';
  return `${(Number(valor) * 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  })}%`;
};

const Fundos = () => {
  const [prestadores, setPrestadores] = useState([]);
  const [prestadorFiltro, setPrestadorFiltro] = useState(null);
  const [competenciaInicio, setCompetenciaInicio] = useState('');
  const [competenciaFim, setCompetenciaFim] = useState('');
  const [tipoFundo, setTipoFundo] = useState('3');
  const [movimentos, setMovimentos] = useState([]);
  const [saldos, setSaldos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalSaque, setModalSaque] = useState(false);
  const [movimentosExpandidos, setMovimentosExpandidos] = useState({});
  const [page, setPage] = useState(1);
  const [ordenacao, setOrdenacao] = useState(null);

  useEffect(() => {
    listarPrestadoresPj()
      .then(resultado => setPrestadores(Array.isArray(resultado) ? resultado : []))
      .catch(err => toast.error(err.message || 'Erro ao carregar prestadores.'));
  }, []);

  const carregarFundos = async () => {
    try {
      setLoading(true);
      const codigoPj = prestadorFiltro ? prestadorFiltro.value : null;
      const filtros = {
        codigoPj: codigoPj || undefined,
        tipoFundo,
        competenciaInicio: competenciaInicio || null,
        competenciaFim: competenciaFim || null,
      };

      const [movimentosResponse, saldosResponse] = await Promise.all([
        listarFundosPj(filtros),
        codigoPj
          ? obterSaldosFundosPj(codigoPj)
          : obterSaldosFundosBulkPj(null),
      ]);

      setMovimentos(Array.isArray(movimentosResponse) ? movimentosResponse : []);
      setSaldos(Array.isArray(saldosResponse) ? saldosResponse : []);
    } catch (err) {
      toast.error(err.message || 'Erro ao carregar fundos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarFundos();
  }, [prestadorFiltro, tipoFundo, competenciaInicio, competenciaFim]);

  useEffect(() => {
    setPage(1);
  }, [prestadorFiltro, tipoFundo, competenciaInicio, competenciaFim]);

  const ordenarPor = key => {
    setOrdenacao(atual => alternarOrdenacao(atual, key));
    setPage(1);
  };

  const prestadorOptions = useMemo(() => (
    prestadores
      .map(prestador => ({
        value: prestador.codigo,
        label: prestador.nome_fantasia || prestador.razao_social,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  ), [prestadores]);

  const saldoPorFundo = fundo => {
    if (!saldos.length) return 0;
    return saldos
      .filter(saldo => saldo.tipo_fundo === fundo)
      .reduce((acc, saldo) => acc + Number(saldo.saldo || 0), 0);
  };

  const saldoAtualPorPj = (codigoPj, fundo) => {
    const item = saldos.find(
      saldo => saldo.codigo_pj === codigoPj && saldo.tipo_fundo === fundo
    );
    return item ? Number(item.saldo || 0) : 0;
  };

  const movimentosOrdenados = useMemo(
    () => ordenarRegistros(movimentos, ordenacao, colunasOrdenacao),
    [movimentos, ordenacao]
  );

  const movimentosAgrupados = useMemo(() => {
    if (prestadorFiltro) return null;
    const mapa = new Map();
    movimentosOrdenados.forEach(item => {
      const chave = item.codigo_pj;
      if (!mapa.has(chave)) {
        mapa.set(chave, {
          codigoPj: chave,
          razaoSocial: item.razao_social || `PJ ${chave}`,
          itens: [],
          totalEntradas: 0,
          totalSaques: 0,
          totalCorrecoes: 0,
        });
      }
      const grupo = mapa.get(chave);
      grupo.itens.push(item);
      const valor = Number(item.valor || 0);
      if (item.tipo_movimento === 'E') grupo.totalEntradas += valor;
      else if (item.tipo_movimento === 'S') grupo.totalSaques += valor;
      else if (item.tipo_movimento === 'C') grupo.totalCorrecoes += valor;
    });
    return Array.from(mapa.values())
      .map(grupo => ({
        ...grupo,
        saldoAtual: saldoAtualPorPj(grupo.codigoPj, tipoFundo),
      }))
      .sort((a, b) => a.razaoSocial.localeCompare(b.razaoSocial));
  }, [movimentosOrdenados, prestadorFiltro, saldos, tipoFundo]);

  const movimentosPaginados = useMemo(() => {
    if (!prestadorFiltro) return [];
    const start = (page - 1) * PAGE_SIZE;
    return movimentosOrdenados.slice(start, start + PAGE_SIZE);
  }, [movimentosOrdenados, page, prestadorFiltro]);

  const toggleMovimentoMobile = codigo => {
    const chave = `${tipoFundo}-${codigo}`;
    setMovimentosExpandidos(prev => ({
      ...prev,
      [chave]: !prev[chave],
    }));
  };

  const tipoFundoLabel = useMemo(() => {
    const encontrado = fundos.find(f => f.key === tipoFundo);
    return encontrado ? encontrado.label : '';
  }, [tipoFundo]);

  // IPCA só incide no Fundo Indenizatório, então a coluna aparece apenas nessa aba.
  const mostrarIpca = tipoFundo === 'F';

  const renderLinhasTabela = lista => (
    lista.map(item => (
      <tr key={item.codigo}>
        <td>{formatarCompetencia(item.competencia) || '-'}</td>
        <td>{dataBr(item.data_movimento)}</td>
        <td>
          <span className={`hpj-badge ${item.tipo_movimento === 'S' ? 'hpj-badge--red' : 'hpj-badge--slate'}`}>
            {tiposMovimento[item.tipo_movimento] || item.tipo_movimento}
          </span>
        </td>
        {mostrarIpca && <td>{formatarIpca(item.indice_ipca)}</td>}
        <td className="hpj-main-text">{moeda(item.valor)}</td>
        <td className="hpj-main-text">{moeda(item.saldo)}</td>
        <td>{item.observacao || '-'}</td>
      </tr>
    ))
  );

  const renderCardsMobile = lista => (
    lista.map(item => {
      const chave = `${tipoFundo}-${item.codigo}`;
      const expandido = !!movimentosExpandidos[chave];
      return (
        <div className="hpj-mobile-card hpj-mobile-card--compact" key={item.codigo}>
          <div className="hpj-mobile-card-head hpj-mobile-card-head--compact">
            <div className="hpj-mobile-title-block">
              <div className="hpj-mobile-title">
                {formatarCompetencia(item.competencia) || '-'}
              </div>
              <div className="hpj-mobile-subtitle">
                Lançamento {dataBr(item.data_movimento)}
              </div>
              <div className="hpj-mobile-card-meta">
                <span className={`hpj-badge ${item.tipo_movimento === 'S' ? 'hpj-badge--red' : 'hpj-badge--slate'}`}>
                  {tiposMovimento[item.tipo_movimento] || item.tipo_movimento}
                </span>
              </div>
              <div className="hpj-mobile-kpi hpj-mobile-kpi--inline">
                {moeda(item.valor)}
              </div>
            </div>
            <Button
              className="hpj-mobile-expand-btn"
              size="sm"
              variant="link"
              aria-expanded={expandido}
              aria-label={expandido ? 'Recolher movimentação' : 'Expandir movimentação'}
              onClick={() => toggleMovimentoMobile(item.codigo)}
            >
              {expandido ? <FaChevronUp /> : <FaChevronDown />}
            </Button>
          </div>
          {expandido && (
            <div className="hpj-mobile-grid hpj-mobile-grid--expanded">
              {mostrarIpca && (
                <div className="hpj-mobile-field">
                  <span>IPCA (%)</span>
                  <strong>{formatarIpca(item.indice_ipca)}</strong>
                </div>
              )}
              <div className="hpj-mobile-field">
                <span>Valor</span>
                <strong>{moeda(item.valor)}</strong>
              </div>
              <div className="hpj-mobile-field">
                <span>Saldo</span>
                <strong>{moeda(item.saldo)}</strong>
              </div>
              <div className="hpj-mobile-field hpj-mobile-field--full">
                <span>Observação</span>
                <strong>{item.observacao || '-'}</strong>
              </div>
            </div>
          )}
        </div>
      );
    })
  );

  const renderGridSegmento = (lista, paginar) => (
    <div className="hpj-card hpj-card--overflow hpj-card--responsive-list">
      <div className="hpj-table-scroll">
        <Table className="hpj-table" responsive>
          <thead>
            <tr>
              <SortableHeader sortKey="competencia" ordenacao={ordenacao} onSort={ordenarPor}>
                Competência
              </SortableHeader>
              <SortableHeader sortKey="data" ordenacao={ordenacao} onSort={ordenarPor}>
                Data lançamento
              </SortableHeader>
              <SortableHeader sortKey="tipo" ordenacao={ordenacao} onSort={ordenarPor}>
                Tipo
              </SortableHeader>
              {mostrarIpca && (
                <SortableHeader sortKey="ipca" ordenacao={ordenacao} onSort={ordenarPor}>
                  IPCA (%)
                </SortableHeader>
              )}
              <SortableHeader sortKey="valor" ordenacao={ordenacao} onSort={ordenarPor}>
                Valor
              </SortableHeader>
              <SortableHeader sortKey="saldo" ordenacao={ordenacao} onSort={ordenarPor}>
                Saldo
              </SortableHeader>
              <SortableHeader sortKey="observacao" ordenacao={ordenacao} onSort={ordenarPor}>
                Observação
              </SortableHeader>
            </tr>
          </thead>
          <tbody>
            {renderLinhasTabela(lista)}
            {!lista.length && (
              <tr>
                <td colSpan={mostrarIpca ? 7 : 6} className="text-center text-muted py-4">
                  {loading ? 'Carregando...' : 'Nenhuma movimentação encontrada'}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <div className="hpj-mobile-list">
        {renderCardsMobile(lista)}
        {!lista.length && (
          <div className="hpj-mobile-empty">
            {loading ? 'Carregando...' : 'Nenhuma movimentação encontrada'}
          </div>
        )}
      </div>
      {paginar && (
        <GridPagination
          page={page}
          pageSize={PAGE_SIZE}
          totalItems={movimentos.length}
          onChange={setPage}
        />
      )}
    </div>
  );

  return (
    <div className="hpj-page">
      <Container fluid className="p-0">
        <div className="hpj-shell hpj-shell--wide">
          <div className="hpj-panel">
            <div className="hpj-title-row">
              <div>
                <div className="hpj-eyebrow">Departamento Jurídico</div>
                <h1 className="hpj-title">Fundos PJ</h1>
                <div className="hpj-subtitle">{movimentos.length} movimentação(ões)</div>
              </div>
              <Button
                className="hpj-btn hpj-btn-primary"
                variant="primary"
                onClick={() => setModalSaque(true)}
              >
                + Registrar Saque
              </Button>
            </div>

            <div className="hpj-filter-row hpj-filter-row--fundos">
              <Form.Group className="hpj-filter-field hpj-filter-field--pj">
                <Form.Label>Prestador PJ</Form.Label>
                <Select
                  classNamePrefix="hpj-user-select"
                  isClearable
                  isSearchable
                  options={prestadorOptions}
                  placeholder="Todos"
                  value={prestadorFiltro}
                  onChange={setPrestadorFiltro}
                />
              </Form.Group>
              <Form.Group className="hpj-filter-field hpj-filter-field--competencia">
                <Form.Label>Competência início</Form.Label>
                <Form.Control
                  className="hpj-filter-control"
                  type="month"
                  value={competenciaInicio}
                  onChange={event => setCompetenciaInicio(event.target.value)}
                />
              </Form.Group>
              <Form.Group className="hpj-filter-field hpj-filter-field--competencia">
                <Form.Label>Competência fim</Form.Label>
                <Form.Control
                  className="hpj-filter-control"
                  type="month"
                  value={competenciaFim}
                  onChange={event => setCompetenciaFim(event.target.value)}
                />
              </Form.Group>
            </div>

            <div className="hpj-fundos-summary" role="tablist">
              {fundos.map(fundo => (
                <button
                  type="button"
                  role="tab"
                  aria-selected={tipoFundo === fundo.key}
                  className={`hpj-fundo-summary-item ${tipoFundo === fundo.key ? 'hpj-fundo-summary-item--active' : ''}`}
                  key={fundo.key}
                  onClick={() => setTipoFundo(fundo.key)}
                >
                  <span>{fundo.label}</span>
                  <strong>{moeda(saldoPorFundo(fundo.key))}</strong>
                </button>
              ))}
            </div>

            {prestadorFiltro
              ? renderGridSegmento(movimentosPaginados, true)
              : (
                <>
                  {!movimentosAgrupados.length && (
                    renderGridSegmento([], false)
                  )}
                  {movimentosAgrupados.map(grupo => (
                    <div className="hpj-grupo-pj" key={grupo.codigoPj}>
                      <div className="hpj-grupo-pj-header">
                        {grupo.razaoSocial} — {tipoFundoLabel}
                      </div>
                      <div className="hpj-grupo-pj-kpis">
                        <div className="hpj-grupo-pj-kpi">
                          <span>Entradas</span>
                          <strong>{moeda(grupo.totalEntradas)}</strong>
                        </div>
                        <div className="hpj-grupo-pj-kpi">
                          <span>Saques</span>
                          <strong>{moeda(grupo.totalSaques)}</strong>
                        </div>
                        <div className="hpj-grupo-pj-kpi">
                          <span>Correções</span>
                          <strong>{moeda(grupo.totalCorrecoes)}</strong>
                        </div>
                        <div className="hpj-grupo-pj-kpi hpj-grupo-pj-kpi--saldo">
                          <span>Saldo atual</span>
                          <strong>{moeda(grupo.saldoAtual)}</strong>
                        </div>
                      </div>
                      {renderGridSegmento(grupo.itens, false)}
                    </div>
                  ))}
                </>
              )
            }
          </div>
        </div>
      </Container>

      <ModalSaque
        show={modalSaque}
        onHide={() => setModalSaque(false)}
        onSalvo={async () => {
          setModalSaque(false);
          await carregarFundos();
        }}
      />
    </div>
  );
};

export default Fundos;
